import {doesResidentNeedForecasting} from '../components/ResidentSentiment/residentSentimentUtilities'
import {isOnboardingTaskStatePendingApproval} from '../Utils/onboardingUtils'
import {onboardingElevatorAmenityFilter} from '../Utils/amenityUtils'
import {DEFAULT_RESIDENT_GROUP, OCCUPANCY_STATUS_FUTURE} from '../constants/ModelConstants'
import {createSelector} from 'reselect'
import {selectRichResidentObjectsFromAppState, getRichResidentObject, isResidentActive} from '../Utils/residentUtils'

/**
 * @param {*} state
 * @return {string|null}
 */
export function selectAuthedUserId(state) {
  return state.Auth.authedUser ? state.Auth.authedUser.attributes.sub : null
}

/**
 *
 * @param {*} state
 * @param {*} residentId
 * @return {*|null}
 */
export function selectOccupancyByResident(state, residentId) {
  const resident = state.Residents.residentsLookup[residentId]
  if (!resident) {
    return null
  }

  return state.Occupancies.occupanciesLookup[resident.residentOccupancyId] || null
}

/**
 * @param {*} state
 * @param {string} actionId
 * @returns {*}
 */
export function selectLoggedActionFromNudgeActions(state, actionId) {
  let action = state.Nudges.loggedNudgesActionsLookup[actionId]
  if (!action) {
    return null
  }
  return state.Nudges.loggedNudgesActionsLookup[action.id]
}

export const selectOnboardingOccupancies = createSelector([state => state.Occupancies.occupanciesLookup], oLookup => {
  return Object.values(oLookup).filter(o => {
    return o.status === OCCUPANCY_STATUS_FUTURE
  })
})

export const selectOnboardingOccupanciesLookup = createSelector([selectOnboardingOccupancies], occuArray => {
  return occuArray.reduce((agr, o) => {
    agr[o.id] = o
    return agr
  }, {})
})

/**
 * @param {*} state
 * @returns {*}
 */
export function checkForOnboardingUnitsRequiringReview(state) {
  const onboardingOccupancies = selectOnboardingOccupancies(state)

  return onboardingOccupancies.some(occupancy => {
    const hasOnboardingTasks = occupancy.onboardingTasks && Array.isArray(occupancy.onboardingTasks) && occupancy.onboardingTasks.length > 0
    const onboardingTasks = hasOnboardingTasks ? occupancy.onboardingTasks : []
    return hasOnboardingTasks
      ? onboardingTasks.reduce((accum, currVal) => {
          if (isOnboardingTaskStatePendingApproval(currVal.state)) {
            accum++
          }
          return accum
        }, 0)
      : 0
  })
}

/**
 * @param {*} state
 * @returns {Array<RichResidentObject>}
 */
export function selectResidentsNeedingForecasting(state) {
  return selectRichResidentObjectsFromAppState(state).filter(resident =>
    doesResidentNeedForecasting(resident, state.Feedback.recentlyForecastedResidents),
  )
}

/**
 * @param {*} state
 * @param {string} residentId
 * @return {Date|null}
 */
export function selectLastTimeResidentForecasted(state, residentId) {
  return state.Feedback.recentlyForecastedResidents[residentId] ? new Date(state.Feedback.recentlyForecastedResidents[residentId]) : null
}

/**
 * @param {*} state
 * @return {Array<*>}
 */
export function selectOnboardingElevatorAmenitiesLookup(state) {
  return Object.values(state.Amenities.amenitiesLookup)
    .filter(onboardingElevatorAmenityFilter)
    .filter(amenity => amenity.isVisible)
    .reduce((accum, currVal) => {
      accum[currVal.id] = currVal
      return accum
    }, {})
}

/**
 * @param {*} state
 * @return {Array<*>}
 */
export const selectBuildingsByActiveOrganization = createSelector([state => state.GlobalAdmin.buildingsLookup], buildingsLookup =>
  Object.values(buildingsLookup),
)

// TODO: this should someday exist in the backend, but for now we will hard-code groups based on the active organization
const HARD_CODED_GROUPS_RXR = [
  {
    value: DEFAULT_RESIDENT_GROUP,
    label: 'Resident',
  },
]
const HARD_CODED_GROUPS_KORMAN = [
  {
    value: 'furnished',
    label: 'Furnished',
  },
  {
    value: DEFAULT_RESIDENT_GROUP,
    label: 'Unfurnished',
  },
]

/**
 * @param {*} state
 * @return {Array<{value: string, label: string}>}
 */
export const selectGroupsByActiveOrganization = state => {
  const activeOrganization = state.GlobalAdmin.organizationsLookup[state.GlobalAdmin.activeOrganizationId]

  switch (activeOrganization?.displayName) {
    case 'Korman':
      return HARD_CODED_GROUPS_KORMAN

    case 'RXR Realty':
    default:
      return HARD_CODED_GROUPS_RXR
  }
}

/**
 * @param {*} state
 * @return {Object<string, string>}
 */
export const selectGroupsLookupByActiveOrganization = createSelector([selectGroupsByActiveOrganization], groupsArray => {
  return groupsArray.reduce((accum, group) => {
    accum[group.value] = group.label
    return accum
  }, {})
})

// -------------------------------- SISTER BUILDINGS SELECTORS BELOW ----------------------------------------

/**
 * Select the active building ID plus all sister building IDs as an array.
 * Useful for fetching data from multiple buildings.
 * Memoized to prevent unnecessary re-renders when values haven't changed
 * @param {*} state
 * @return {Array<string>}
 */
export const selectActiveBuildingAndSisterBuildingIds = createSelector(
  [state => state.Buildings.activeBuildingId, state => state.Buildings.sisterBuildingsLookup],
  (activeBuildingId, sisterBuildingsLookup) => {
    const sisterBuildings = sisterBuildingsLookup[activeBuildingId] || []
    const sisterBuildingIds = sisterBuildings.map(sb => sb.id).filter(Boolean)
    return activeBuildingId ? [activeBuildingId, ...sisterBuildingIds] : sisterBuildingIds
  },
)

/**
 * Select a combined buildings lookup that includes both the active building's buildingsLookup
 * and sister buildings from sisterBuildingsLookup (keyed by building ID).
 * Useful for checking per-building properties like onboardingSchema for any resident's building.
 * @param {*} state
 * @return {Object<string, Object>}
 */
export const selectCombinedBuildingsLookup = createSelector(
  [state => state.Buildings.buildingsLookup, state => state.Buildings.activeBuildingId, state => state.Buildings.sisterBuildingsLookup],
  (buildingsLookup, activeBuildingId, sisterBuildingsLookup) => {
    const sisterBuildings = sisterBuildingsLookup[activeBuildingId] || []
    const sisterBuildingsById = sisterBuildings.reduce((acc, sb) => {
      if (sb.id) {
        acc[sb.id] = sb
      }
      return acc
    }, {})
    return {...sisterBuildingsById, ...buildingsLookup}
  },
)

// -------------------------------- COMBINED DATA SELECTORS FOR AMENITIES/EVENTS ----------------------------------------

/**
 * Factory function to create memoized combined lookup selectors.
 * Combines data from active building with sister building data.
 *
 * @param {Function} getMainLookup - Function to extract main lookup from state
 * @param {Function} getSisterLookup - Function to extract sister building lookup from state
 * @return {Function} Memoized selector returning combined lookup
 */
const createCombinedLookupSelector = (getMainLookup, getSisterLookup) =>
  createSelector([getMainLookup, getSisterLookup], (mainLookup, sisterLookup) => ({...mainLookup, ...sisterLookup}))

/**
 * Select combined residents lookup (active building + sister buildings).
 * Used specifically for amenities and events where residents from sister buildings
 * may book shared amenities.
 */
export const selectCombinedResidentsLookup = createCombinedLookupSelector(
  state => state.Residents.residentsLookup,
  state => state.SisterBuildingData.residentsLookup,
)

/**
 * Select combined units lookup (active building + sister buildings).
 */
export const selectCombinedUnitsLookup = createCombinedLookupSelector(
  state => state.Units.unitsLookup,
  state => state.SisterBuildingData.unitsLookup,
)

/**
 * Select combined occupancies lookup (active building + sister buildings).
 */
export const selectCombinedOccupanciesLookup = createCombinedLookupSelector(
  state => state.Occupancies.occupanciesLookup,
  state => state.SisterBuildingData.occupanciesLookup,
)

/**
 * Select combined staff lookup (active building + sister buildings).
 * Used for amenities where staff from any sister building may manage reservations.
 */
export const selectCombinedStaffLookup = createCombinedLookupSelector(
  state => state.Staff.staffLookup,
  state => state.SisterBuildingData.staffLookup,
)

/**
 * Select RichResidentObjects from combined data (active building + sister buildings).
 * Used for amenities and events where residents from sister buildings may book shared amenities.
 * @returns {Array<RichResidentObject>}
 */
export const selectCombinedRichResidentObjects = createSelector(
  [selectCombinedResidentsLookup, selectCombinedOccupanciesLookup, selectCombinedUnitsLookup],
  (rLookup, oLookup, uLookup) => {
    return Object.values(rLookup).map(r => getRichResidentObject(rLookup, oLookup, uLookup, r.id))
  },
)

/**
 * Get active residents from combined data (active building + sister buildings).
 * Used specifically for amenities and events where residents from sister buildings
 * may book shared amenities.
 * @returns {Array<RichResidentObject>}
 */
export const getCombinedActiveResidents = createSelector([selectCombinedRichResidentObjects], richResidentsArray => {
  return richResidentsArray.filter(r => isResidentActive(r))
})
